import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import type { DevelopmentSummaryModel, JiraEpicSync } from '@/domain/models/development.js';
import type { BusinessProcessResponse, ProjectUserResponse } from '@/domain/models/project.js';
import { useApp } from '@/presentation/hooks/use-app.js';
import type { EditFormState } from '@/presentation/hooks/use-development-edit-options.js';
import { LinkedDevelopmentField } from '@/presentation/pages/developments/development-detail/components/dialogs/linked-development-field.js';
import { JiraEpicStatus } from '@/presentation/pages/developments/development-detail/components/jira-epic-status.js';
import { COMPLEXITY_OPTIONS } from '@/presentation/pages/developments/development-detail/utils/is-superior-estimate.js';
import { jiraRetryLabel } from '@/presentation/pages/developments/utils/jira-sync-pending.js';
import { DEVELOPMENT_CATEGORIES } from '@/shared/utils/format-status.js';
import type { Complexity, DevelopmentCategory } from '@/shared/utils/format-status.js';

interface Props {
    open: boolean;
    onClose: () => void;
    onSave: () => void;
    saving: boolean;
    error: string | null;
    fullEditStatus: boolean;
    linkOnly: boolean;
    form: EditFormState;
    onFieldChange: <K extends keyof EditFormState>(key: K, value: EditFormState[K]) => void;
    businessProcesses: BusinessProcessResponse[];
    consultores: ProjectUserResponse[];
    keyUsers: ProjectUserResponse[];
    linkableDevs: DevelopmentSummaryModel[];
    jiraEpicKey: string | null;
    jiraEpicUrl?: string | null;
    epicSync?: JiraEpicSync;
}

export function EditDevelopmentDialog({
    open,
    onClose,
    onSave,
    saving,
    error,
    fullEditStatus,
    linkOnly,
    form,
    onFieldChange,
    businessProcesses,
    consultores,
    keyUsers,
    linkableDevs,
    jiraEpicKey,
    jiraEpicUrl = null,
    epicSync = { status: 'NONE', nextAttemptAt: null }
}: Props) {
    const { translate } = useApp();
    const linkedDevField = (
        <LinkedDevelopmentField label={translate('devForm.fieldLinkedDev')} options={linkableDevs} value={form.linkedDev} onChange={(v) => onFieldChange('linkedDev', v)} />
    );

    return (
        <Dialog open={open} onClose={onClose} disableEscapeKeyDown={saving} maxWidth="sm" fullWidth>
            <DialogTitle>{translate('modals.editDevTitle')}</DialogTitle>
            <DialogContent>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    {error && <Alert severity="error">{error}</Alert>}
                    <Box>
                        <Typography variant="caption" color="text.secondary">
                            {translate('devDetail.jiraEpicKey')}
                        </Typography>
                        <Typography variant="body2" fontWeight={500} component="div" sx={{ display: 'flex', alignItems: 'center', minHeight: 40 }}>
                            <JiraEpicStatus
                                status={epicSync.status}
                                epicKey={jiraEpicKey}
                                epicUrl={jiraEpicUrl}
                                epicStatusName={null}
                                creatingLabel={translate('devDetail.jiraEpicCreating')}
                                retryLabel={jiraRetryLabel(translate, epicSync.nextAttemptAt)}
                                variant="field"
                            />
                        </Typography>
                    </Box>
                    {linkOnly ? (
                        linkedDevField
                    ) : (
                        <>
                            {fullEditStatus && (
                                <>
                                    <TextField
                                        label={translate('devForm.fieldTitle')}
                                        value={form.title}
                                        onChange={(e) => onFieldChange('title', e.target.value)}
                                        size="small"
                                        fullWidth
                                    />
                                    <TextField
                                        label={translate('devForm.fieldDescription')}
                                        value={form.description}
                                        onChange={(e) => onFieldChange('description', e.target.value)}
                                        size="small"
                                        fullWidth
                                        multiline
                                        rows={3}
                                    />
                                </>
                            )}
                            <TextField
                                select
                                label={translate('devForm.fieldBusinessProcess')}
                                value={form.businessProcessId}
                                onChange={(e) => onFieldChange('businessProcessId', e.target.value)}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">{translate('devForm.selectBusinessProcess')}</MenuItem>
                                {businessProcesses.map((bp) => (
                                    <MenuItem key={bp.id} value={bp.id}>
                                        {bp.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label={translate('devForm.fieldConsultor')}
                                value={form.consultorId}
                                onChange={(e) => onFieldChange('consultorId', e.target.value)}
                                size="small"
                                fullWidth
                            >
                                {consultores.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                label={translate('devForm.fieldKeyUser')}
                                value={form.keyUserId}
                                onChange={(e) => onFieldChange('keyUserId', e.target.value)}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="">{translate('devForm.selectUser')}</MenuItem>
                                {keyUsers.map((u) => (
                                    <MenuItem key={u.id} value={u.id}>
                                        {u.name}
                                    </MenuItem>
                                ))}
                            </TextField>
                            <TextField
                                select
                                required
                                label={translate('devForm.fieldCategory')}
                                value={form.category}
                                onChange={(e) => onFieldChange('category', e.target.value as DevelopmentCategory | '')}
                                size="small"
                                fullWidth
                            >
                                <MenuItem value="" disabled>
                                    {translate('devForm.selectCategory')}
                                </MenuItem>
                                {DEVELOPMENT_CATEGORIES.map((c) => (
                                    <MenuItem key={c} value={c}>
                                        {translate(`devForm.categoryLabels.${c}`)}
                                    </MenuItem>
                                ))}
                            </TextField>
                            {fullEditStatus && (
                                <>
                                    {linkedDevField}
                                    <TextField
                                        select
                                        label={translate('devForm.fieldComplexity')}
                                        value={form.complexity}
                                        onChange={(e) => onFieldChange('complexity', e.target.value as Complexity)}
                                        size="small"
                                        fullWidth
                                    >
                                        <MenuItem value="" disabled>
                                            {translate('estimate.selectComplexity')}
                                        </MenuItem>
                                        {COMPLEXITY_OPTIONS.map((c) => (
                                            <MenuItem key={c} value={c}>
                                                {translate(`complexityLabels.${c}`)}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                    <TextField
                                        type="number"
                                        label={translate('estimate.estimatedHours')}
                                        placeholder={translate('estimate.hoursPlaceholder')}
                                        value={form.hours}
                                        onChange={(e) => onFieldChange('hours', e.target.value)}
                                        size="small"
                                        fullWidth
                                    />
                                </>
                            )}
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={saving}>
                    {translate('common.cancel')}
                </Button>
                <Button onClick={() => onSave()} variant="contained" sx={{ color: 'white' }} disabled={saving}>
                    {saving ? translate('common.saving') : translate('devForm.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
